<template>
  <div
    class="guests-wizard-summary"
    :class="{ desktop: $isDesktop }"
    data-test="search-wizard-summary"
  >
    <transition name="fade">
      <translate
        v-if="showMessage"
        class="msg"
        tag="div"
        data-test="guests-wizard-err"
      >
        Please choose your dates first
      </translate>
    </transition>

    <section
      data-test="search-wizard-guests"
      class="guests-row"
      :class="[
        { disabled, 'showing-message': showMessage, 'has-dogs': hasDogs },
      ]"
    >
      <div class="guests-cell adults">
        <img
          v-if="guests.adults === 1"
          class="icon"
          src="@/assets/images/adult.svg"
          width="6"
          height="19"
          alt=""
        />
        <img
          v-else-if="guests.adults >= 4"
          class="icon"
          src="@/assets/images/group.svg"
          width="18"
          height="19"
          alt=""
        />
        <img
          v-else
          class="icon"
          src="@/assets/images/adults.svg"
          width="13"
          height="19"
          alt=""
        />
        <translate :tt="{ adults: guests.adults }"> {adults} adults </translate>
      </div>

      <div
        :class="{ 'has-children': hasChildren }"
        class="guests-cell children"
      >
        <img
          v-if="guests.childAges.length === 1"
          class="icon"
          src="@/assets/images/child.svg"
          width="6"
          height="19"
          alt=""
        />
        <img
          v-else
          class="icon"
          src="@/assets/images/children.svg"
          width="15"
          height="19"
          alt=""
        />
        <section class="children-summary">
          <span>
            <translate :tt="{ childrenCount: guests.childAges.length }">
              {childrenCount} children
            </translate>
          </span>
          <translate
            v-if="hasChildren"
            class="ages-summary"
            :tt="{ childAges: guests.childAges.join(', ') }"
          >
            (Aged {childAges})
          </translate>
        </section>
      </div>

      <div v-if="hasDogs" class="guests-cell dogs">
        <img
          class="icon"
          src="@/assets/images/dog-green.svg"
          width="6"
          height="19"
          alt=""
        />
        <translate :tt="{ dogs: guests.dogs }"> {dogs} dogs </translate>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import type { SearchParty } from '../../../search/schemas'

@Component
class GuestsWizardSummary extends Vue {
  @Prop({ required: true, type: Object })
  guests: SearchParty

  @Prop()
  disabled: boolean

  @Prop()
  showMessage: boolean

  get hasChildren(): boolean {
    return this.guests.childAges.length > 0
  }

  get hasDogs(): boolean {
    return Boolean(this.guests.dogs)
  }
}

export default toNative(GuestsWizardSummary)
</script>
<style scoped>
.guests-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 0.25em;
  column-gap: 1em;
}

.icon {
  height: 1.2em;
  margin-right: 0.35em;
  width: auto;
}

.guests-row.has-dogs .children {
  grid-column: 1/3;
}

.guests-row.has-dogs .dogs {
  grid-column: 2/3;
  grid-row: 1/2;
}

.guests-cell {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
}

.guests-cell.adults span {
  flex-shrink: 0;
}

.ages-summary {
  color: var(--colour-text-light);
  font-size: var(--small);
}

.children {
  color: var(--colour-text-light);
}

.children .icon {
  filter: grayscale(100%) opacity(50%);
  height: 1em;
  margin-top: 0.25em;
}

.has-children .icon {
  filter: none;
}

.has-children {
  color: var(--colour-text-default);
}

.children-summary :deep(strong) {
  font-weight: normal;
}

.has-children .children-summary :deep(strong) {
  font-weight: bold;
}

.children-summary {
  display: flex;
  flex-direction: column;
}

.guests-wizard-summary {
  position: relative;
}

.showing-message.guests-row {
  opacity: 0;
}

.msg {
  align-items: center;
  border-radius: 0.5em;
  color: var(--colour-red);
  display: flex;
  font-size: var(--body);
  justify-content: center;
  position: absolute;
  width: 100%;
  z-index: 1;
}

/* desktop & tablet */
.desktop .msg {
  align-items: left;
  border-radius: 0;
  position: relative;
}

.desktop .showing-message.guests-row,
.desktop .ages-summary {
  display: none;
}

.desktop .guests-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
</style>
